import { useState, useEffect } from 'react'
import Modal from 'components/Modal'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import { useFormik } from 'formik'
import * as yup from 'yup'
import cn from 'classnames'
import fetchWithAuthorization from 'utils/fetchWithAuthorization'
import { succeededBienAVendreQuizz } from 'redux/features/utilisateur/slice'
import { useAppDispatch } from 'redux/hooks'
import FenetresAide from 'enums/FenetresAide'
import QuizzJson from 'assets/default/quizz.json'
import Translation from 'assets/default/translation.json'
import Urls from 'assets/default/url.json'
import s from './QuizzAccesCiblerBienAVendre.module.scss'

// Form values of the quizz
interface QuizzValues {
  [k: string]: string[]
}

// From Quizz.json
interface Quizz {
  question: string
  answers: string[]
  correctAnswers: string[]
}

// Number of valid answer to succeed the quizz
const MIN_SUCCESS_QUIZZ = 10

interface Props {
  show: boolean
  onHide: () => void
}

const QuizzAccesCiblerBienAVendre = ({ show, onHide }: Props) => {
  const dispatch = useAppDispatch()
  const [validAnswer, setValidAnswer] = useState<number | undefined>(undefined)
  // Create initalValues, ex:
  // {
  //   quizz-0: undefined,
  //   quizz-1: 2,
  //   quizz-2: 0
  // }
  const initialValues: QuizzValues = QuizzJson.reduce<any>(
    (obj, _, index) => ({ ...obj, [`quizz-${index}`]: undefined }),
    {}
  )
  const validationSchema = yup.object().shape(
    QuizzJson.reduce<any>(
      (obj, _, index) => ({
        ...obj,
        [`quizz-${index}`]: yup
          .array(yup.string())
          .min(1)
          .required('Vous devez choisir au moins une réponse'),
      }),
      {}
    )
  )

  const countValidAnswers = (values: QuizzValues) =>
    QuizzJson.reduce((total, { correctAnswers }: Quizz, index: number) => {
      if (
        values[`quizz-${index}`].every((answer) =>
          correctAnswers.includes(answer)
        )
      )
        return total + 1
      return total
    }, 0)

  const onSubmit = async (values: QuizzValues) => {
    const answerCount = countValidAnswers(values)

    setValidAnswer(answerCount)

    // Validate the quizz on the backend side if succeeded
    if (answerCount >= MIN_SUCCESS_QUIZZ) {
      try {
        const response = await fetchWithAuthorization(
          `/api/help/${FenetresAide.PremierAccesCiblerBienAVendre}/mark-as-seen`,
          { method: 'POST' }
        )
        if (!response.ok) {
          throw Error('Une erreur est survenue dans la validation du quizz')
        }
      } catch (error) {
        console.error((error as Error).toString())
      }
    }
  }

  const { handleSubmit, handleChange, values, errors, touched } = useFormik({
    onSubmit,
    initialValues,
    validationSchema,
  })

  const handleClick = () => {
    // Close the modal only if the quizz was a success
    if (validAnswer !== undefined && validAnswer >= MIN_SUCCESS_QUIZZ) onHide()
  }

  const hasSubmit = validAnswer !== undefined

  useEffect(() => {
    // Valider le quizz dans la classe utilisateur, si reussi
    if (validAnswer && validAnswer >= MIN_SUCCESS_QUIZZ)
      dispatch(succeededBienAVendreQuizz())
  }, [validAnswer, dispatch])

  return (
    <Modal
      show={show}
      title={`Quizz pour accéder à l'espace «Cibler Biens à Vendre» de ${Translation.common.produit}`}
      additionalModalClassNames={s.quizzAccesCiblerBienAVendre}
      showClose={false}
      onCloseButtonClick={handleClick}
      body={
        <Form noValidate onSubmit={handleSubmit}>
          {QuizzJson.map(
            ({ question, answers, correctAnswers }, indexQuizz) => {
              const nameId = `quizz-${indexQuizz}`

              return (
                <Form.Group className="mb-4" key={nameId}>
                  <Form.Label className={s.label} htmlFor={nameId}>
                    {`${indexQuizz + 1}. ${question}`}
                    {correctAnswers.length > 1 && (
                      <i> (Plusieurs réponses possibles)</i>
                    )}
                  </Form.Label>
                  <div id={nameId} onChange={handleChange}>
                    {answers.map((answer, indexAnswer) => {
                      const isFormSubmitted = () => validAnswer !== undefined
                      const isQuizzSuccess = () =>
                        validAnswer !== undefined &&
                        validAnswer >= MIN_SUCCESS_QUIZZ
                      const isCorrectAnswer = () =>
                        correctAnswers.includes(`${indexAnswer}`)
                      const isCheckedByUser = () =>
                        values[nameId].includes(`${indexAnswer}`)

                      return (
                        <Form.Check
                          className={cn(s.check, {
                            'text-success':
                              // Green the right answers
                              isFormSubmitted() &&
                              ((isQuizzSuccess() && isCorrectAnswer()) ||
                                (!isQuizzSuccess() &&
                                  isCorrectAnswer() &&
                                  isCheckedByUser())),
                            'text-danger':
                              // Red the wrong answer
                              isFormSubmitted() &&
                              !isCorrectAnswer() &&
                              isCheckedByUser(),
                          })}
                          id={`${nameId}-${indexAnswer}`}
                          key={`${nameId}-${indexAnswer}`}
                          type="checkbox"
                          name={nameId}
                          label={`${indexAnswer + 1}. ${answer}`}
                          value={indexAnswer}
                          disabled={hasSubmit}
                        />
                      )
                    })}
                  </div>
                  {touched[nameId] && errors[nameId] && (
                    <Form.Text className="text-danger">
                      {errors[nameId]}
                    </Form.Text>
                  )}
                </Form.Group>
              )
            }
          )}
          {!hasSubmit && (
            <Button
              className="px-4 mx-auto d-block"
              variant="primary"
              type="submit"
            >
              Valider
            </Button>
          )}
          {hasSubmit && validAnswer && validAnswer >= MIN_SUCCESS_QUIZZ && (
            <>
              <p className={s.feedback}>
                {`Bravo, vous avez ${validAnswer} bonnes réponses ! Vous êtes prêt(e) à utiliser
          l'espace «Cibler un bien à vendre». Bonnes recherches !`}
              </p>
              <Button className="px-4 mx-auto d-block" onClick={onHide}>
                Accéder à {Translation.common.produit}
              </Button>
            </>
          )}
          {hasSubmit && validAnswer && validAnswer < MIN_SUCCESS_QUIZZ && (
            <>
              <p className={s.feedback}>
                {`Encore un effort... vous n'avez que ${validAnswer} bonnes réponses ! Allez
            vite perfectionner vos connaissances grâce au webinar de formation
            et aux vidéos eLearning. A très bientôt !`}
              </p>
              <div className="d-flex justify-content-center">
                {typeof Urls.pagesOmega.formation === 'string' && (
                  <Button
                    className="mx-3"
                    href={Urls.pagesOmega.formation}
                    target="_blank"
                    size="lg"
                  >
                    M’inscrire au webinar
                  </Button>
                )}
                {typeof Urls.pagesOmega.elearning === 'string' && (
                  <Button
                    className="mx-3"
                    size="lg"
                    href={Urls.pagesOmega.elearning}
                    target="_blank"
                  >
                    Voir les tutoriels e-learning
                  </Button>
                )}
              </div>
            </>
          )}
        </Form>
      }
    />
  )
}

export default QuizzAccesCiblerBienAVendre
